import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { ArrowRight, CheckCircle, FileText, Building, Shield, Clock } from 'lucide-react';
import axios from 'axios';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(null);
  const [services, setServices] = useState([
    { id: 'temp-1', name: 'ITR-1', price: '₹999', desc: 'For salaried individuals', category: 'Income Tax' },
    { id: 'temp-2', name: 'ITR-2', price: '₹1,999', desc: 'Capital gains & multiple properties', category: 'Income Tax' },
    { id: 'temp-3', name: 'ITR-3', price: '₹3,999', desc: 'Business/profession income', category: 'Income Tax' },
    { id: 'temp-4', name: 'ITR-4', price: '₹1,499', desc: 'Presumptive income', category: 'Income Tax' },
    { id: 'temp-5', name: 'GST Registration', price: '₹1,999', desc: 'New GST registration', category: 'GST' },
    { id: 'temp-6', name: 'GST Filing', price: '₹999/mo', desc: 'Monthly GST returns', category: 'GST' },
    { id: 'temp-7', name: 'MSME Registration', price: '₹1,499', desc: 'Udyam registration', category: 'Business' },
    { id: 'temp-8', name: 'Trademark', price: '₹4,999', desc: 'Trademark registration', category: 'Business' },
    { id: 'temp-9', name: 'Company Registration', price: '₹9,999', desc: 'Pvt Ltd registration', category: 'Business' },
  ]);

  // Fetch actual services from backend if user is logged in
  React.useEffect(() => {
    if (user) {
      const fetchServices = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/services`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.data && response.data.length > 0) {
            setServices(response.data.map(s => ({
              ...s,
              price: s.price ? `₹${s.price}` : s.price,
              desc: s.description || s.desc
            })));
          }
        } catch (error) {
          console.error('Error fetching services:', error);
        }
      };
      fetchServices();
    }
  }, [user]);

  const features = [
    { icon: Clock, title: 'Quick Processing', desc: 'Get your returns filed within 24-48 hours' },
    { icon: Shield, title: 'Secure & Safe', desc: 'Your data is encrypted and stored securely' },
    { icon: FileText, title: 'Expert CA Support', desc: 'Chartered Accountants review every filing' },
    { icon: Building, title: 'All Services', desc: 'ITR, GST, Business Registration & more' },
  ];

  const handleGetStarted = async (serviceId) => {
    if (!user) {
      navigate('/register');
      return;
    }

    // If no serviceId provided (main CTA) or serviceId is an event object, navigate to services page
    if (!serviceId || typeof serviceId !== 'string') {
      navigate('/services');
      return;
    }

    // User is logged in - add service and go to dashboard
    setAdding(serviceId);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/service-requests`,
        { service_id: serviceId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error adding service:', error);
      alert('Failed to add service. Please try again.');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4" data-testid="hero-section">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" data-testid="hero-title">
            Tax Filing Made <span className="text-blue-600">Simple</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" data-testid="hero-subtitle">
            File your ITR, GST returns, and register your business with expert CA assistance - all online, all hassle-free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleGetStarted}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
              data-testid="cta-start-filing"
            >
              Start Filing Now <ArrowRight size={20} />
            </button>
            {!user && (
              <Link 
                to="/login"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition"
                data-testid="cta-login"
              >
                Already a User? Login
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-white" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose ITRQUICK?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="text-center p-6" data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <feature.icon className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4" data-testid="services-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Our Services</h2>
          <p className="text-center text-gray-600 mb-12">Comprehensive tax and business solutions for individuals and businesses</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div key={service.name} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition border border-gray-200" data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">{service.category}</div>
                  <div className="text-2xl font-bold text-gray-900">{service.price}</div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                <p className="text-gray-600 mb-4">{service.desc}</p>
                <button 
                  onClick={() => handleGetStarted(service.id)}
                  disabled={adding === service.id}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid={`service-cta-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {adding === service.id ? 'Adding...' : (user ? 'Add Service' : 'Get Started')} <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Highlights */}
      <section className="py-16 px-4 bg-blue-600 text-white" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Transparent Pricing, No Hidden Charges</h2>
          <p className="text-xl mb-8 opacity-90">Starting from ₹999 - Professional service at affordable prices</p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle size={24} />
              <span className="text-lg">Expert CA Review</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle size={24} />
              <span className="text-lg">24-48hr Processing</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle size={24} />
              <span className="text-lg">Secure Tax Locker</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to File Your Taxes?</h2>
          <p className="text-xl text-gray-600 mb-8">Join thousands of satisfied customers who trust ITRQUICK</p>
          <button 
            onClick={handleGetStarted}
            className="bg-blue-600 text-white px-12 py-4 rounded-lg text-xl font-semibold hover:bg-blue-700 transition inline-flex items-center gap-2"
            data-testid="cta-final-start"
          >
            Start Now <ArrowRight size={24} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-2xl font-bold mb-4">ITRQUICK</div>
          <p className="text-gray-400 mb-4">Professional tax filing and business services</p>
          <div className="mb-4">
            <a 
              href="/admin-login" 
              className="text-gray-500 hover:text-red-400 text-xs transition"
            >
              Admin Portal
            </a>
          </div>
          <p className="text-sm text-gray-500">© 2025 ITRQUICK. All rights reserved.</p>
        </div>
      </footer>

      {/* Emergent Badge - Tiny */}
      <div className="emergent-badge">
        <img src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4" alt="" />
      </div>
    </div>
  );
};

export default HomePage;